/**
 * sanitizeCredits — defensive scrubber for legacy/corrupted credit entries.
 *
 * Background (04/06/2026): a player in production reported a "NaN €" being
 * displayed everywhere on the Banque page — total debt, individual loan
 * "Restant dû", loan proposal modal — making the screen unusable.
 *
 * Root cause: when a credit was created with a non-finite `principal` (e.g.
 * because `proposal.amount` was NaN due to a bad upstream computation in a
 * legacy save), the `remainingPrincipal` field propagated NaN through every
 * `reduce` and `Math.max(0, …)` call downstream.
 *
 * `JSON.stringify(NaN)` serializes to `null`, but `undefined` values are
 * silently dropped from the object — so on rehydrate the field may be
 * `null`, `undefined`, or NaN (when set in-memory after a bad mutation).
 *
 * This helper normalises every credit:
 *   - Numeric fields default to safe values (0 or the loan principal).
 *   - `remainingPrincipal` falls back to `principal` if available, else 0.
 *   - `remainingMonths` falls back to `durationMonths`, else 0.
 *   - Credits where `principal` AND `remainingPrincipal` are both unusable
 *     are DROPPED entirely (they can't be salvaged).
 *
 * Pure function — does not mutate the input. Returns a new array.
 */

function safeFinite(v, fallback = 0) {
  // null and undefined → use fallback (semantic "missing value"). For all
  // other inputs, attempt numeric coercion; bail to fallback on NaN/Infinity.
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * @param {Array<Object>|undefined|null} credits — raw credits array.
 * @returns {Array<Object>} sanitised credits (may be empty).
 */
export function sanitizeCredits(credits) {
  if (!Array.isArray(credits)) return [];
  const cleaned = [];
  for (const c of credits) {
    if (!c || typeof c !== 'object') continue;
    const principal = safeFinite(c.principal, 0);
    let remainingPrincipal = safeFinite(c.remainingPrincipal, principal);
    if (remainingPrincipal < 0) remainingPrincipal = 0;
    if (principal <= 0 && remainingPrincipal <= 0) {
      // Cannot salvage — drop entirely.
      continue;
    }
    let annualRate = safeFinite(c.annualRate, 0);
    // 19/07/2026 — taux stocké en pourcent (ex. crédit fiscal IS `4.5`) :
    // aucun taux légitime ne dépasse 100 %, on convertit en décimal.
    if (annualRate > 1) annualRate = annualRate / 100;
    const monthlyPayment = safeFinite(c.monthlyPayment, 0);
    const remainingMonths = safeFinite(c.remainingMonths, safeFinite(c.durationMonths, 0));
    // 19/07/2026 — user prod : crédit messagerie créé sans
    // `remainingPrincipal` → tombé à 0 au 1er amortissement alors que des
    // mensualités restaient dues → « Solder le crédit » grisé à vie.
    // Réparation : on ré-estime le restant dû depuis les mensualités.
    if (remainingPrincipal <= 0 && remainingMonths > 0 && principal > 0) {
      remainingPrincipal = Math.min(principal, Math.round(monthlyPayment * remainingMonths));
      if (remainingPrincipal <= 0) remainingPrincipal = principal;
    }
    cleaned.push({
      ...c,
      principal: principal > 0 ? principal : remainingPrincipal,
      annualRate,
      durationMonths: safeFinite(c.durationMonths, 0),
      monthlyPayment,
      remainingMonths,
      remainingPrincipal,
    });
  }
  return cleaned;
}

/**
 * Returns true iff the input array contains at least one credit with an
 * obviously broken numeric field.
 */
export function hasCorruptCredit(credits) {
  if (!Array.isArray(credits)) return false;
  return credits.some(c => !c || !Number.isFinite(Number(c?.remainingPrincipal))
    || !Number.isFinite(Number(c?.principal))
    || !Number.isFinite(Number(c?.monthlyPayment))
    || !Number.isFinite(Number(c?.annualRate))
    // 19/07/2026 — taux en pourcent (4.5 = crédit fiscal legacy).
    || Number(c?.annualRate) > 1
    // 19/07/2026 — restant dû à 0 avec des mensualités encore dues
    // (crédit messagerie sans remainingPrincipal, écrasé à 0 par le
    // 1er amortissement).
    || (Number(c?.remainingPrincipal) <= 0 && Number(c?.remainingMonths) > 0 && Number(c?.principal) > 0));
}
